import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { DashboardNavigationService } from 'src/app/navigation/dashboard-navigation.service';
import { NotificationService } from 'src/app/notifications/notification.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { DashboardAccessOptions, DashboardResponsiveApiModelSparse, SaveDashboardModalData } from 'src/shared/models/dashboard.model';
import { DashboardService } from '../dashboard.service';
import { DashboardAccessComponent } from '../modals/dashboard-access/dashboard-access.component';
import { DashboardAccessService } from '../modals/dashboard-access/dashboard-access.service';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { AnalyticsService, ConfigurationService } from '@niceltd/cxone-client-platform-services';
import { TemplateListModalComponent } from '../modals/template-list-modal/template-list-modal.component';
import { SubscriptionModalComponent } from '../modals/subscription-modal/subscription-modal.component';
import { SubscriptionModalService, SubscriptionOptions } from '../modals/subscription-modal/subscription-modal.service';
import { FullSubscriptionSchedule } from 'src/shared/models/subscriptions.model';
import { SubscriptionSchedulerService } from 'src/app/reusables/subscription-scheduler/subscription-scheduler.service';

@Component({
    selector: 'cv-launch-cxone-dash-admin',
    templateUrl: './launch-cxone-dash-admin.component.html',
    styleUrls: ['./launch-cxone-dash-admin.component.scss']
})
export class LaunchCXOneDashAdminComponent implements OnInit, OnDestroy {

    filterValue = '';
    columns = ['Favorite', 'DashboardName', 'Shared', 'Owners', 'CreatedBy', 'DashboardDescription', 'LastViewDate', 'Modified', 'Actions'];
    showAssignTool = false;
    showScheduler = false;
    dashboardsToAssign: DashboardResponsiveApiModelSparse[] = [];
    hasOwnLegacyDashboard = false;
    selectedDashboards: DashboardResponsiveApiModelSparse[] = [];
    schedulerId: number;
    schedulerKey: string;
    subscriptionTitle: string;
    numGridRows = 0;
    favoritesOnly = true;
    showCurrentlyOpen = false;
    shareModal: NgbModalRef;
    showGrid = true;
    modalRef: NgbModalRef = null;
    dashboardSubscriptions: Map<string, FullSubscriptionSchedule>;

    get hasCreatePermission() {
        return this.dashboardService.permissions !== undefined ? this.dashboardService.permissions["launch_cxone_dash"] : false;
    }

    private _subscriptions: Subscription[] = [];

    constructor(
        private dashboardService: DashboardService,
        private dashboardNavigationService: DashboardNavigationService,
        private cvLocalizationService: CVLocalizationService,
        private dashboardAccessService: DashboardAccessService,
        private notificationService: NotificationService,
        private navigationService: NavigationService,
        private cxAnalytics: AnalyticsService,
        private cxConfig: ConfigurationService,
        private config: CvConfigurationService,
        private ngbModal: NgbModal,
        private subscriptionService: SubscriptionModalService,
        private schedulerService: SubscriptionSchedulerService,
    ) {
        this._subscriptions.push(this.dashboardService.newDashboardCreated.subscribe(() => {
            sendMessageCXOne({dashboardSelected: true});
            if (window.location.href !== "/Dashboard/launch_cxone_dash.aspx")
                window.location.href = "/Dashboard/launch_cxone_dash.aspx";
        }));

        this.config.isCxOne = true;

        this.loadSubscriptions();
    }

    async ngOnInit() {
        await this.cvLocalizationService.GetSection("Dashboards_Landing");
        this.dashboardService.getUserWorkspace();
    }
    
    canCreateNewDashboard() {
        const dashboardOpened = this.dashboardService.dashboards.filter(d => d != null);
        return dashboardOpened.length < this.dashboardService.maxAttachedDashboards;
    }

    openCopyDialog() {
        if (!this.hasCreatePermission || this.selectedDashboards.length !== 1)
            return;

        const data: SaveDashboardModalData = {
            dashboardName: 'Copy of ' + (this.selectedDashboards[0].DashboardName ? this.selectedDashboards[0].DashboardName : ''),
            description: this.selectedDashboards[0].DashboardDescription ? this.selectedDashboards[0].DashboardDescription : '',
            folder: this.selectedDashboards[0].DashboardGroup ? this.selectedDashboards[0].DashboardGroup : '',
            key: this.selectedDashboards[0].DashboardKey,
            iscxone: this.selectedDashboards[0].IsCXOne
        };

        this.dashboardService.openSaveDashboardModal(data, 'CopyDashboard', false).subscribe(async dashboard => {
            if (typeof(dashboard) !== undefined && dashboard !== null) {
                await this.dashboardService.clearWorkspace(this.dashboardService.workspaceKey, true);
                this.dashboardService.clearDashboardsFromWorkspace();
                await this.dashboardService.addDashboardToWorkspace(this.dashboardService.workspaceKey, dashboard, true, true);
                this.dashboardNavigationService.navigateToCXOneDashboardView();
            }
        });
    }

    openEditDetailsDialog() {
        if (!this.hasCreatePermission || this.selectedDashboards.length !== 1)
            return;

        const data: SaveDashboardModalData = {
            dashboardName: this.selectedDashboards[0].DashboardName ? this.selectedDashboards[0].DashboardName : '',
            description: this.selectedDashboards[0].DashboardDescription ? this.selectedDashboards[0].DashboardDescription : '',
            folder: this.selectedDashboards[0].DashboardGroup ? this.selectedDashboards[0].DashboardGroup : '',
            key: this.selectedDashboards[0].DashboardKey,
            iscxone: this.selectedDashboards[0].IsCXOne
        };

        this.dashboardService.openSaveDashboardModal(data, 'EditDashboard', false);
    }

    async openNewDashboardDialog() {
        if (!this.hasCreatePermission)
            return;

        const data: SaveDashboardModalData = {
            dashboardName: '',
            description: '',
            folder: 'CXOne Default',
            key: this.dashboardService.workspaceKey,
            iscxone: true // all dashboards created on this page will be for cxone
        };

        this.dashboardService.openSaveDashboardModal(data, 'NewDashboard', false);
    }

    openFromTemplate() {
        this.navigationService.showOverlay = true;
        this.modalRef = this.ngbModal.open(TemplateListModalComponent, { size: "from-template", centered: true, scrollable: true, modalDialogClass: 'cx-one-ui' });
        this.modalRef.result.finally(() => this.navigationService.showOverlay = false);
    }

    openShareModal(dashboards: DashboardResponsiveApiModelSparse[]) {
        const option: DashboardAccessOptions = {
            dashboard: dashboards[0],
        };
        this.shareModal = this.dashboardAccessService.openModal(option, DashboardAccessComponent);
        this.shareModal.result.then(async (dashboard: DashboardResponsiveApiModelSparse) => {
            const dashboardIndex = this.dashboardService.availableDashboards.findIndex(d => d.DashboardKey === dashboard.DashboardKey);
            this.dashboardService.availableDashboards[dashboardIndex] = dashboard;
            this.dashboardService.availableDashboardsChanged.emit();
            this.showGrid = false;
            setTimeout(() => {
                this.showGrid = true;
            }, 1);
        });
    }

    public async loadSubscriptions() {
        this.dashboardSubscriptions = new Map<string, FullSubscriptionSchedule>();
        await this.dashboardService.loadExistingDashboardsAndGroups();

        this.dashboardService.availableDashboards.forEach(async d => {
            const subscription = await this.schedulerService.getSubscriptionScheduleByRelatedKeyAndID(d.DashboardKey, d.DashboardID);

            this.dashboardSubscriptions.set(d.DashboardKey, subscription);
        });
    }

    openSubscribe() {
        const dashboard = this.selectedDashboards[0];

        const existingSubscription = this.dashboardSubscriptions.get(dashboard.DashboardKey);
        const option: SubscriptionOptions = {
            embedMode: false,
            reportType: existingSubscription?.ReportSubscriptionSchedule ? existingSubscription.ReportSubscription.ReportType : 0,
            subscriptionId: existingSubscription?.ReportSubscriptionSchedule
                ? existingSubscription.ReportSubscription.ReportSubscriptionID : -1,
            existingSchedule: existingSubscription?.ReportSubscriptionSchedule ? existingSubscription.ReportSubscription : null,
            subscriptionTitle: existingSubscription?.ReportSubscription.ReportSubscriptionName.length > 0
                ? existingSubscription.ReportSubscription.ReportSubscriptionName : dashboard.DashboardName,
            relatedId: dashboard.DashboardID,
            dashboardKey: dashboard.DashboardKey,
        };
        const modalInstance = this.subscriptionService.openModal(option, SubscriptionModalComponent);
        modalInstance.componentInstance.saved.subscribe((schedule: FullSubscriptionSchedule) => 
            this.dashboardSubscriptions.set(schedule.ReportSubscription.RelatedKey, schedule));
    }

    closeSubscribe() {
        // update dashboard list
        this.dashboardService.availableDashboardsChanged.emit();
        this.showScheduler = false;
    }

    getLocalizationString(key: string, defaultValue: string, numPlaceholder?: number) {
        if (typeof (numPlaceholder) === undefined || numPlaceholder === null)
            return this.cvLocalizationService.getCachedText("Dashboards_Landing", key, defaultValue);

        else {
            return this.cvLocalizationService.Format(this.cvLocalizationService.getCachedText(
                "Dashboards_Landing", key, defaultValue), numPlaceholder);
        }
    }

    ngOnDestroy() {
        this._subscriptions.forEach(sub => sub.unsubscribe());
        this.shareModal.close();
    }
}
